import {
  AppVersion,
  AppVersionCreatePayload,
  AppVersionPlatform,
  AppVersionUpdatePayload,
} from "@/domain/app-versions.interface";
import { axios, transformAxiosError } from "@/utils/axios.create";

const getCurrentVersion = async (platform: string): Promise<AppVersion> => {
  return axios
    .get("/app-versions/current?", {
      params: {
        platform,
      },
    })
    .then((res) => res.data.body)
    .catch((err) => Promise.reject(err));
};

export type AppVersionsFindAllPayload = {
  sortField: string;
  sortOrder: number;
  pageNumber: number;
  pageSize: number;
  search: string;
  platform: string;
  isCurrent: string;
};

const findAll = async ({
  sortField,
  sortOrder,
  pageNumber,
  pageSize,
  search,
  platform,
  isCurrent,
}: AppVersionsFindAllPayload): Promise<{
  list: AppVersion[];
  totalCount: number;
}> => {
  const queryParams = new URLSearchParams({
    search,
    pageNumber: String(pageNumber + 1),
    pageSize: String(pageSize),
    sortField,
    sortOrder: sortOrder === 1 ? "asc" : "desc",
  });
  if (platform) queryParams.append("platform", platform);
  if (isCurrent) queryParams.append("isCurrent", isCurrent);

  return axios
    .get(`/app-versions/search?${queryParams.toString()}`)
    .then((res) => ({
      list: res.data.body.list as AppVersion[],
      totalCount: res.data.body.totalCount as number,
    }))
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const createOne = async (
  payload: AppVersionCreatePayload
): Promise<AppVersion> => {
  return axios
    .post("/app-versions", payload)
    .then((res) => res.data.body as AppVersion)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const updateOne = async ({
  id,
  payload,
}: {
  id: string;
  payload: AppVersionUpdatePayload;
}): Promise<AppVersion> => {
  return axios
    .put(`/app-versions/${id}`, payload)
    .then((res) => res.data.body as AppVersion)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const setCurrent = async ({
  id,
  platform,
}: {
  id: string;
  platform: AppVersionPlatform;
}): Promise<{ success: boolean; message: string }> => {
  return axios
    .patch(`/app-versions/${id}/current`, { platform })
    .then((res) => res.data.body as { success: boolean; message: string })
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const archiveOne = async (id: string): Promise<void> => {
  return axios
    .delete(`/app-versions/${id}`)
    .then(() => undefined)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

export const AppVersionService = {
  getCurrentVersion,
  findAll,
  createOne,
  updateOne,
  setCurrent,
  archiveOne,
};
